import React, { useState, useEffect, useCallback } from "react";
import Content from "../../../layout/content/Content";
import Head from "../../../layout/head/Head";
import {
  Card,
  Badge,
  Spinner,
  Alert,
  UncontrolledTooltip,
  Modal,
  ModalBody,
  ModalHeader,
  ModalFooter,
  FormGroup,
  Label,
  Input,
} from "reactstrap";
import {
  Block,
  BlockBetween,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Icon,
  Button,
} from "../../../components/Component";
import UserProfileAside from "./UserProfileAside";
import superAdminService from "../../../api/super-admin/superAdminService";
import LoadingSpinner from "../../../components/loading/LoadingSpinner";

const JOB_DESCRIPTIONS = {
  "invoice-scheduler": {
    title: "Invoice Auto-Generation",
    description: "Automatically generates invoices for all eligible customers based on their billing cycle and active module subscriptions.",
    schedule: "Runs daily at 2:00 AM IST",
    steps: [
      "Finds all customers with Billing Mode = Auto and Billing Status = Active",
      "Checks if invoice is due based on billing frequency (Monthly / Quarterly / Half-Yearly / Yearly)",
      "Skips customers who already have an invoice for the current billing period",
      "Calculates subtotal from all active module and bundle subscriptions",
      "Applies coupon discounts if any active coupons are assigned",
      "Deducts advance payments from available balance",
      "Calculates applicable taxes (CGST / SGST)",
      "Creates Invoice, Invoice Line Items, Advance Adjustments, and Receivable records",
    ],
    safeToRerun: true,
    safeNote: "Safe to re-run — already invoiced customers are automatically skipped.",
  },
  "receivables-scheduler": {
    title: "Receivables Auto-Hold",
    description: "Automatically places customer accounts on hold when their receivables exceed the configured threshold or payment is overdue.",
    schedule: "Runs daily at 1:00 AM IST",
    steps: [
      "Marks all invoices with due date passed and balance > 0 as 'Overdue'",
      "Updates aging metrics on all open receivables (aging days, overdue days)",
      "Assigns aging buckets: Current, 0-30 Days, 31-60 Days, 61-90 Days, or 90+ Days",
      "Finds all customers with overdue receivables and calculates max overdue days",
      "For customers with Auto-Hold enabled: if overdue days exceed threshold, sets Billing Status to 'Hold'",
      "Held accounts may lose access to certain modules until payment is received",
      "Logs all hold actions in Access Control Logs for audit trail",
    ],
    safeToRerun: true,
    safeNote: "Safe to re-run — only updates accounts that meet overdue criteria.",
    warning: "Customers exceeding their overdue threshold will be placed on Hold and may lose module access.",
  },
  "coupon-scheduler": {
    title: "Coupon Auto-Expiry",
    description: "Automatically expires coupons that have passed their validity end date, preventing further usage.",
    schedule: "Runs daily at 12:00 AM (Midnight) IST",
    steps: [
      "Finds all coupons where validity end date (validTo) has passed",
      "Filters only coupons that are still in Active status (skips already Expired/Inactive)",
      "Changes status of each matched coupon to 'Expired'",
      "Creates an audit log entry for each expired coupon with coupon code and expiry date",
      "Expired coupons can no longer be applied to any customer billing",
    ],
    safeToRerun: false,
    safeNote: "Irreversible — expired coupons cannot be automatically reactivated.",
  },
};

const STAT_DESCRIPTIONS = {
  active: "Jobs currently being processed by a worker",
  completed: "Total jobs that finished successfully (auto-cleared after completion)",
  failed: "Total jobs that encountered an error during execution",
  waiting: "Jobs queued and waiting to be picked up by a worker",
};

const SchedulerDashboard = () => {
  const [sm, updateSm] = useState(false);
  const [mobileView, setMobileView] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [jobsSummary, setJobsSummary] = useState([]);
  const [selectedQueue, setSelectedQueue] = useState(null);
  const [jobHistory, setJobHistory] = useState([]);
  const [historyStatus, setHistoryStatus] = useState("completed");
  const [historyLoading, setHistoryLoading] = useState(false);
  const [retriggerLoading, setRetriggerLoading] = useState(null);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  const [confirmModal, setConfirmModal] = useState({ open: false, queueName: null });
  const [scheduleModal, setScheduleModal] = useState({ open: false, job: null });
  const [scheduleHour, setScheduleHour] = useState(0);
  const [scheduleMinute, setScheduleMinute] = useState(0);
  const [scheduleLoading, setScheduleLoading] = useState(false);

  useEffect(() => {
    const viewChange = () => {
      if (window.innerWidth < 990) {
        setMobileView(true);
      } else {
        setMobileView(false);
        updateSm(false);
      }
    };
    viewChange();
    window.addEventListener("load", viewChange);
    window.addEventListener("resize", viewChange);
    return () => {
      window.removeEventListener("resize", viewChange);
      window.removeEventListener("load", viewChange);
    };
  }, []);

  const fetchJobsSummary = useCallback(async () => {
    try {
      setRefreshing(true);
      setError(null);
      const res = await superAdminService.getSchedulerJobsSummary();
      if (res?.status === 200 && res?.data) {
        setJobsSummary(res.data);
      }
    } catch (err) {
      setError("Failed to load scheduler jobs summary");
      console.error(err);
    } finally {
      setRefreshing(false);
      setInitialLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchJobsSummary();
    const interval = setInterval(fetchJobsSummary, 30000);
    return () => clearInterval(interval);
  }, [fetchJobsSummary]);

  const fetchJobHistory = async (queueName, status) => {
    try {
      setHistoryLoading(true);
      const res = await superAdminService.getSchedulerJobHistory(queueName, status, 20);
      if (res?.status === 200 && res?.data) {
        setJobHistory(res.data);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleJobClick = (queueName) => {
    setSelectedQueue(queueName);
    setHistoryStatus("completed");
    fetchJobHistory(queueName, "completed");
  };

  const handleHistoryStatusChange = (status) => {
    setHistoryStatus(status);
    if (selectedQueue) {
      fetchJobHistory(selectedQueue, status);
    }
  };

  const openRetriggerConfirm = (e, queueName) => {
    e.stopPropagation();
    setConfirmModal({ open: true, queueName });
  };

  const handleRetrigger = async () => {
    const queueName = confirmModal.queueName;
    setConfirmModal({ open: false, queueName: null });
    try {
      setRetriggerLoading(queueName);
      setSuccessMsg(null);
      const res = await superAdminService.retriggerSchedulerJob(queueName);
      if (res?.status === 200) {
        const jobInfo = JOB_DESCRIPTIONS[queueName];
        setSuccessMsg(
          `"${jobInfo?.title || queueName}" triggered successfully! Job ID: ${res.data?.jobId}. The job is now queued and will be processed shortly.`
        );
        setTimeout(() => setSuccessMsg(null), 8000);
        fetchJobsSummary();
      }
    } catch (err) {
      setError("Failed to retrigger job. Please try again.");
      setTimeout(() => setError(null), 5000);
    } finally {
      setRetriggerLoading(null);
    }
  };

  const openScheduleModal = async (e, job) => {
    e.stopPropagation();
    try {
      const res = await superAdminService.getSchedulerConfig();
      const configs = res?.data || [];
      const config = configs.find((c) => c.queueName === job.queueName);
      if (config) {
        const parts = config.cronExpression.trim().split(/\s+/);
        setScheduleMinute(parseInt(parts[0], 10) || 0);
        setScheduleHour(parseInt(parts[1], 10) || 0);
      } else {
        setScheduleHour(0);
        setScheduleMinute(0);
      }
    } catch {
      setScheduleHour(0);
      setScheduleMinute(0);
    }
    setScheduleModal({ open: true, job });
  };

  const handleSaveSchedule = async () => {
    if (!scheduleModal.job) return;
    const cronExpression = `${scheduleMinute} ${scheduleHour} * * *`;
    try {
      setScheduleLoading(true);
      const res = await superAdminService.updateSchedulerJobSchedule({
        queueName: scheduleModal.job.queueName,
        cronExpression,
      });
      if (res?.status === 200) {
        setSuccessMsg(`Schedule for "${scheduleModal.job.jobName}" updated to ${formatCronDisplay(scheduleHour, scheduleMinute)} successfully!`);
        setTimeout(() => setSuccessMsg(null), 6000);
        setScheduleModal({ open: false, job: null });
        fetchJobsSummary();
      } else {
        setError(res?.message || "Failed to update schedule.");
        setTimeout(() => setError(null), 5000);
      }
    } catch (err) {
      setError("Failed to update schedule. Please try again.");
      setTimeout(() => setError(null), 5000);
    } finally {
      setScheduleLoading(false);
    }
  };

  const formatCronDisplay = (hour, minute) => {
    const period = hour < 12 ? "AM" : "PM";
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    const displayMinute = minute.toString().padStart(2, "0");
    return `${displayHour}:${displayMinute} ${period}`;
  };

  const formatDate = (date) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  const formatDuration = (ms) => {
    if (!ms && ms !== 0) return "N/A";
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${Math.floor(ms / 60000)}m ${Math.round((ms % 60000) / 1000)}s`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Completed": return "success";
      case "Failed": return "danger";
      case "Error": return "warning";
      default: return "secondary";
    }
  };

  const getTotalCounts = () => {
    return jobsSummary.reduce(
      (acc, job) => ({
        active: acc.active + (job.counts?.active || 0),
        completed: acc.completed + (job.counts?.completed || 0),
        failed: acc.failed + (job.counts?.failed || 0),
      }),
      { active: 0, completed: 0, failed: 0 }
    );
  };

  const totals = getTotalCounts();
  const confirmJobInfo = JOB_DESCRIPTIONS[confirmModal.queueName] || {};

  return (
    <React.Fragment>
      <Head title="Scheduler Dashboard" />
      <Content>
        <Card className="card-bordered">
          <div className="card-aside-wrap">
            <div
              className={`card-aside card-aside-left user-aside toggle-slide toggle-slide-left toggle-break-lg ${
                sm ? "content-active" : ""
              }`}
            >
              <UserProfileAside updateSm={updateSm} sm={sm} />
            </div>
            <div className="card-inner card-inner-lg">
              {sm && mobileView && (
                <div
                  className="toggle-overlay"
                  onClick={() => updateSm(!sm)}
                ></div>
              )}

              <BlockHead size="lg">
                <BlockBetween>
                  <BlockHeadContent>
                    <BlockTitle tag="h4">Scheduler Jobs Dashboard</BlockTitle>
                    <div className="nk-block-des">
                      <p>
                        Monitor and manage automated background tasks. These jobs run on a daily schedule
                        to handle invoicing, receivables, and coupon management. Data refreshes automatically every 30 seconds.
                      </p>
                    </div>
                  </BlockHeadContent>
                  <BlockHeadContent className="align-self-start d-lg-none">
                    <Button
                      className={`toggle btn btn-icon btn-trigger mt-n1 ${
                        sm ? "active" : ""
                      }`}
                      onClick={() => updateSm(!sm)}
                    >
                      <Icon name="menu-alt-r"></Icon>
                    </Button>
                  </BlockHeadContent>
                  <BlockHeadContent>
                    <Button
                      color="primary"
                      size="sm"
                      onClick={fetchJobsSummary}
                      disabled={refreshing}
                      id="refresh-btn"
                    >
                      {refreshing ? <Spinner size="sm" style={{ marginRight: "4px" }} /> : <Icon name="reload" />}
                      <span>Refresh</span>
                    </Button>
                    <UncontrolledTooltip target="refresh-btn" placement="left">
                      Fetch latest job status from Redis queues
                    </UncontrolledTooltip>
                  </BlockHeadContent>
                </BlockBetween>
              </BlockHead>

              {error && (
                <Alert color="danger" className="mb-3" fade>
                  <Icon name="alert-circle" style={{ marginRight: "8px" }} />
                  {error}
                </Alert>
              )}
              {successMsg && (
                <Alert color="success" className="mb-3" fade>
                  <Icon name="check-circle" style={{ marginRight: "8px" }} />
                  {successMsg}
                </Alert>
              )}

              {/* Stat Cards */}
              <Block>
                <div className="row g-gs mb-4">
                  <div className="col-sm-4">
                    <div className="card card-bordered card-full" id="stat-active">
                      <div className="card-inner">
                        <div className="d-flex align-items-center justify-content-between mb-1">
                          <h6 className="title" style={{ color: "#1ee0ac" }}>Active</h6>
                          <Icon name="loader" style={{ color: "#1ee0ac", fontSize: "1.2rem" }} />
                        </div>
                        <div className="card-amount">
                          <span className="amount" style={{ color: "#1ee0ac", fontSize: "2rem" }}>
                            {totals.active}
                          </span>
                        </div>
                        <p className="text-muted" style={{ fontSize: "12px", marginTop: "4px", marginBottom: 0 }}>
                          {STAT_DESCRIPTIONS.active}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="col-sm-4">
                    <div className="card card-bordered card-full" id="stat-completed">
                      <div className="card-inner">
                        <div className="d-flex align-items-center justify-content-between mb-1">
                          <h6 className="title" style={{ color: "#6576ff" }}>Completed</h6>
                          <Icon name="check-circle" style={{ color: "#6576ff", fontSize: "1.2rem" }} />
                        </div>
                        <div className="card-amount">
                          <span className="amount" style={{ color: "#6576ff", fontSize: "2rem" }}>
                            {totals.completed}
                          </span>
                        </div>
                        <p className="text-muted" style={{ fontSize: "12px", marginTop: "4px", marginBottom: 0 }}>
                          {STAT_DESCRIPTIONS.completed}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="col-sm-4">
                    <div className="card card-bordered card-full" id="stat-failed">
                      <div className="card-inner">
                        <div className="d-flex align-items-center justify-content-between mb-1">
                          <h6 className="title" style={{ color: "#e85347" }}>Failed</h6>
                          <Icon name="alert-circle" style={{ color: "#e85347", fontSize: "1.2rem" }} />
                        </div>
                        <div className="card-amount">
                          <span className="amount" style={{ color: "#e85347", fontSize: "2rem" }}>
                            {totals.failed}
                          </span>
                        </div>
                        <p className="text-muted" style={{ fontSize: "12px", marginTop: "4px", marginBottom: 0 }}>
                          {STAT_DESCRIPTIONS.failed}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </Block>

              {/* Jobs Table */}
              <Block>
                <div className="nk-block-head nk-block-head-sm">
                  <div className="nk-block-head-content">
                    <h6 className="nk-block-title">Scheduled Jobs</h6>
                    <p className="text-muted" style={{ fontSize: "13px" }}>
                      Click on any job row to view its execution history. Use the play button to manually trigger a job outside its schedule.
                    </p>
                  </div>
                </div>
                {initialLoading ? (
                  <LoadingSpinner tip="Loading job data from Redis queues..." />
                ) : (
                  <div className="nk-tb-list nk-tb-ulist is-compact card card-bordered">
                    <div className="nk-tb-item nk-tb-head">
                      <div className="nk-tb-col">
                        <span className="sub-text">Job Name</span>
                      </div>
                      <div className="nk-tb-col tb-col-md">
                        <span className="sub-text" id="col-schedule">Schedule</span>
                        <UncontrolledTooltip target="col-schedule" placement="top">
                          How often this job runs automatically
                        </UncontrolledTooltip>
                      </div>
                      <div className="nk-tb-col tb-col-md">
                        <span className="sub-text" id="col-nextrun">Next Run</span>
                        <UncontrolledTooltip target="col-nextrun" placement="top">
                          When this job will next execute automatically
                        </UncontrolledTooltip>
                      </div>
                      <div className="nk-tb-col tb-col-md">
                        <span className="sub-text" id="col-lastrun">Last Run</span>
                        <UncontrolledTooltip target="col-lastrun" placement="top">
                          When this job last finished executing
                        </UncontrolledTooltip>
                      </div>
                      <div className="nk-tb-col">
                        <span className="sub-text" id="col-status">Status</span>
                        <UncontrolledTooltip target="col-status" placement="top">
                          Result of the most recent execution
                        </UncontrolledTooltip>
                      </div>
                      <div className="nk-tb-col tb-col-md">
                        <span className="sub-text" id="col-stats">Queue Stats</span>
                        <UncontrolledTooltip target="col-stats" placement="top">
                          W = Waiting, A = Active, C = Completed, F = Failed
                        </UncontrolledTooltip>
                      </div>
                      <div className="nk-tb-col nk-tb-col-tools" style={{ minWidth: "90px" }}>
                        <span className="sub-text">Actions</span>
                      </div>
                    </div>
                    {jobsSummary.map((job, idx) => {
                      const jobDesc = JOB_DESCRIPTIONS[job.queueName] || {};
                      return (
                        <div
                          key={idx}
                          className={`nk-tb-item ${selectedQueue === job.queueName ? "bg-lighter" : ""}`}
                          style={{ cursor: "pointer" }}
                          onClick={() => handleJobClick(job.queueName)}
                        >
                          <div className="nk-tb-col">
                            <div>
                              <span className="tb-lead">{job.jobName}</span>
                              <span className="d-block text-muted" style={{ fontSize: "11px" }}>
                                {jobDesc.description
                                  ? jobDesc.description.substring(0, 80) + (jobDesc.description.length > 80 ? "..." : "")
                                  : job.queueName}
                              </span>
                            </div>
                          </div>
                          <div className="nk-tb-col tb-col-md">
                            <span>{job.schedule}</span>
                          </div>
                          <div className="nk-tb-col tb-col-md">
                            <span>{formatDate(job.nextRun)}</span>
                          </div>
                          <div className="nk-tb-col tb-col-md">
                            <span>{formatDate(job.lastRun)}</span>
                          </div>
                          <div className="nk-tb-col">
                            <Badge
                              color={getStatusColor(job.lastStatus)}
                              className="badge-dot badge-dot-xs"
                            >
                              {job.lastStatus}
                            </Badge>
                          </div>
                          <div className="nk-tb-col tb-col-md">
                            <span className="text-muted" style={{ fontSize: "12px" }}>
                              W:{job.counts?.waiting || 0}{" "}
                              A:{job.counts?.active || 0}{" "}
                              C:{job.counts?.completed || 0}{" "}
                              F:{job.counts?.failed || 0}
                            </span>
                          </div>
                          <div className="nk-tb-col nk-tb-col-tools">
                            <ul className="nk-tb-actions gx-1">
                              <li>
                                <Button
                                  id={`edit-schedule-${idx}`}
                                  color="light"
                                  size="sm"
                                  className="btn-icon btn-round"
                                  onClick={(e) => openScheduleModal(e, job)}
                                >
                                  <Icon name="edit" />
                                </Button>
                                <UncontrolledTooltip target={`edit-schedule-${idx}`} placement="left">
                                  Change the run time for this job
                                </UncontrolledTooltip>
                              </li>
                              <li>
                                <Button
                                  id={`retrigger-${idx}`}
                                  color="primary"
                                  size="sm"
                                  className="btn-icon btn-round"
                                  onClick={(e) => openRetriggerConfirm(e, job.queueName)}
                                  disabled={retriggerLoading === job.queueName}
                                >
                                  {retriggerLoading === job.queueName ? (
                                    <Spinner size="sm" />
                                  ) : (
                                    <Icon name="play" />
                                  )}
                                </Button>
                                <UncontrolledTooltip target={`retrigger-${idx}`} placement="left">
                                  Manually run this job now (outside its regular schedule)
                                </UncontrolledTooltip>
                              </li>
                            </ul>
                          </div>
                        </div>
                      );
                    })}
                    {jobsSummary.length === 0 && (
                      <div className="nk-tb-item">
                        <div className="nk-tb-col" style={{ textAlign: "center", padding: "20px" }}>
                          <span className="text-muted">No scheduler jobs found. Ensure the admin service is running and connected to Redis.</span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </Block>

              {/* Job History */}
              {selectedQueue && (
                <Block>
                  <div className="nk-block-head nk-block-head-sm mt-4">
                    <BlockBetween>
                      <BlockHeadContent>
                        <h6 className="nk-block-title">
                          Job History — {jobsSummary.find((j) => j.queueName === selectedQueue)?.jobName || selectedQueue}
                        </h6>
                        <p className="text-muted" style={{ fontSize: "13px" }}>
                          Last 20 execution records. Toggle between completed and failed runs to investigate issues.
                        </p>
                      </BlockHeadContent>
                      <BlockHeadContent>
                        <ul className="nk-block-tools g-3">
                          <li>
                            <Button
                              color={historyStatus === "completed" ? "primary" : "outline-primary"}
                              size="xs"
                              onClick={() => handleHistoryStatusChange("completed")}
                            >
                              Completed
                            </Button>
                          </li>
                          <li>
                            <Button
                              color={historyStatus === "failed" ? "danger" : "outline-danger"}
                              size="xs"
                              onClick={() => handleHistoryStatusChange("failed")}
                            >
                              Failed
                            </Button>
                          </li>
                        </ul>
                      </BlockHeadContent>
                    </BlockBetween>
                  </div>
                  {historyLoading ? (
                    <LoadingSpinner tip="Loading job history..." />
                  ) : (
                    <div className="nk-tb-list nk-tb-ulist is-compact card card-bordered">
                      <div className="nk-tb-item nk-tb-head">
                        <div className="nk-tb-col">
                          <span className="sub-text" id="hist-jobid">Job ID</span>
                          <UncontrolledTooltip target="hist-jobid" placement="top">
                            Unique identifier assigned to each job execution
                          </UncontrolledTooltip>
                        </div>
                        <div className="nk-tb-col">
                          <span className="sub-text" id="hist-queued">Queued At</span>
                          <UncontrolledTooltip target="hist-queued" placement="top">
                            When the job was added to the queue
                          </UncontrolledTooltip>
                        </div>
                        <div className="nk-tb-col tb-col-md">
                          <span className="sub-text" id="hist-finished">Finished At</span>
                          <UncontrolledTooltip target="hist-finished" placement="top">
                            When the job finished processing
                          </UncontrolledTooltip>
                        </div>
                        <div className="nk-tb-col">
                          <span className="sub-text" id="hist-duration">Duration</span>
                          <UncontrolledTooltip target="hist-duration" placement="top">
                            How long the job took to process
                          </UncontrolledTooltip>
                        </div>
                        <div className="nk-tb-col">
                          <span className="sub-text">Status</span>
                        </div>
                        <div className="nk-tb-col tb-col-lg">
                          <span className="sub-text" id="hist-error">Error Details</span>
                          <UncontrolledTooltip target="hist-error" placement="top">
                            Error message if the job failed
                          </UncontrolledTooltip>
                        </div>
                      </div>
                      {jobHistory.map((item, idx) => (
                        <div key={idx} className="nk-tb-item">
                          <div className="nk-tb-col">
                            <span className="tb-sub">#{item.jobId}</span>
                          </div>
                          <div className="nk-tb-col">
                            <span>{formatDate(item.timestamp)}</span>
                          </div>
                          <div className="nk-tb-col tb-col-md">
                            <span>{formatDate(item.finishedOn)}</span>
                          </div>
                          <div className="nk-tb-col">
                            <span>{formatDuration(item.duration)}</span>
                          </div>
                          <div className="nk-tb-col">
                            <Badge
                              color={item.status === "Completed" ? "success" : "danger"}
                              className="badge-dot badge-dot-xs"
                            >
                              {item.status}
                            </Badge>
                          </div>
                          <div className="nk-tb-col tb-col-lg">
                            <span className="text-danger" style={{ fontSize: "12px" }}>
                              {item.error || "-"}
                            </span>
                          </div>
                        </div>
                      ))}
                      {jobHistory.length === 0 && (
                        <div className="nk-tb-item">
                          <div className="nk-tb-col" style={{ textAlign: "center", padding: "20px" }}>
                            <span className="text-muted">
                              {historyStatus === "completed"
                                ? "No completed job records found. Jobs will appear here after their first successful execution."
                                : "No failed job records found. This is a good sign — all executions have been successful!"}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </Block>
              )}

              {/* Job Details Section — what each trigger does */}
              {!initialLoading && jobsSummary.length > 0 && (
                <Block>
                  <div className="nk-block-head nk-block-head-sm mt-4">
                    <div className="nk-block-head-content">
                      <h6 className="nk-block-title">What Each Job Does</h6>
                      <p className="text-muted" style={{ fontSize: "13px" }}>
                        Detailed breakdown of operations performed when each job runs (scheduled or manually triggered).
                      </p>
                    </div>
                  </div>
                  <div className="row g-gs">
                    {jobsSummary.map((job, idx) => {
                      const info = JOB_DESCRIPTIONS[job.queueName] || {};
                      return (
                        <div key={idx} className="col-md-4">
                          <div className="card card-bordered h-100">
                            <div className="card-inner">
                              <div className="d-flex align-items-center justify-content-between mb-2">
                                <h6 style={{ fontSize: "14px", marginBottom: 0 }}>{info.title || job.jobName}</h6>
                                <Badge color="outline-primary" pill style={{ fontSize: "10px" }}>
                                  {job.schedule}
                                </Badge>
                              </div>
                              <p className="text-muted" style={{ fontSize: "12px", marginBottom: "8px" }}>
                                {info.description}
                              </p>
                              <div style={{ borderTop: "1px solid #e5e9f2", paddingTop: "8px" }}>
                                <strong style={{ fontSize: "12px", color: "#364a63" }}>
                                  When triggered, this job will:
                                </strong>
                                <ol style={{ fontSize: "12px", color: "#526484", paddingLeft: "16px", marginTop: "6px", marginBottom: "8px" }}>
                                  {(info.steps || []).map((step, sIdx) => (
                                    <li key={sIdx} style={{ marginBottom: "4px" }}>{step}</li>
                                  ))}
                                </ol>
                              </div>
                              <div
                                className="p-2 mt-2"
                                style={{
                                  background: info.safeToRerun ? "#e2f5ea" : "#fff3cd",
                                  borderRadius: "4px",
                                  border: `1px solid ${info.safeToRerun ? "#1ee0ac" : "#ffc107"}`,
                                }}
                              >
                                <span style={{ fontSize: "11px" }}>
                                  {info.safeToRerun ? (
                                    <Icon name="check-circle" style={{ color: "#1ee0ac", marginRight: "4px", fontSize: "13px" }} />
                                  ) : (
                                    <Icon name="alert" style={{ color: "#ffc107", marginRight: "4px", fontSize: "13px" }} />
                                  )}
                                  {info.safeNote}
                                </span>
                              </div>
                              {info.warning && (
                                <div
                                  className="p-2 mt-2"
                                  style={{
                                    background: "#ffeae9",
                                    borderRadius: "4px",
                                    border: "1px solid #e85347",
                                  }}
                                >
                                  <span style={{ fontSize: "11px", color: "#e85347" }}>
                                    <Icon name="alert-circle" style={{ marginRight: "4px", fontSize: "13px" }} />
                                    {info.warning}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </Block>
              )}
            </div>
          </div>
        </Card>

        {/* Edit Schedule Modal */}
        <Modal isOpen={scheduleModal.open} toggle={() => setScheduleModal({ open: false, job: null })} centered size="lg">
          <ModalHeader toggle={() => setScheduleModal({ open: false, job: null })}>
            Change Schedule — {scheduleModal.job?.jobName}
          </ModalHeader>
          <ModalBody>
            <p className="text-muted" style={{ fontSize: "13px", marginBottom: "16px" }}>
              Set the daily run time for this job. The new schedule will take effect immediately and persist across restarts.
            </p>
            <div className="row g-3">
              <div className="col-6">
                <FormGroup>
                  <Label style={{ fontSize: "13px" }}>Hour (0–23)</Label>
                  <Input
                    type="select"
                    value={scheduleHour}
                    onChange={(e) => setScheduleHour(parseInt(e.target.value, 10))}
                  >
                    {Array.from({ length: 24 }, (_, i) => {
                      const period = i < 12 ? "AM" : "PM";
                      const display = i === 0 ? 12 : i > 12 ? i - 12 : i;
                      return (
                        <option key={i} value={i}>
                          {display}:00 {period} ({i})
                        </option>
                      );
                    })}
                  </Input>
                </FormGroup>
              </div>
              <div className="col-6">
                <FormGroup>
                  <Label style={{ fontSize: "13px" }}>Minute</Label>
                  <Input
                    type="select"
                    value={scheduleMinute}
                    onChange={(e) => setScheduleMinute(parseInt(e.target.value, 10))}
                  >
                    {[0, 15, 30, 45].map((m) => (
                      <option key={m} value={m}>:{m.toString().padStart(2, "0")}</option>
                    ))}
                  </Input>
                </FormGroup>
              </div>
            </div>
            <div className="p-2 mt-2" style={{ background: "#f5f6fa", borderRadius: "4px", border: "1px solid #dbdfea", fontSize: "13px" }}>
              <strong>New schedule: </strong>Daily at {formatCronDisplay(scheduleHour, scheduleMinute)}
              <br />
              <span className="text-muted" style={{ fontSize: "11px" }}>
                Cron: {scheduleMinute} {scheduleHour} * * *
              </span>
            </div>
          </ModalBody>
          <ModalFooter className="bg-light">
            <Button color="light" size="sm" onClick={() => setScheduleModal({ open: false, job: null })}>
              Cancel
            </Button>
            <Button color="primary" size="sm" onClick={handleSaveSchedule} disabled={scheduleLoading}>
              {scheduleLoading ? <Spinner size="sm" style={{ marginRight: "4px" }} /> : <Icon name="check" style={{ marginRight: "4px" }} />}
              Save Schedule
            </Button>
          </ModalFooter>
        </Modal>

        {/* Retrigger Confirmation Modal */}
        <Modal isOpen={confirmModal.open} toggle={() => setConfirmModal({ open: false, queueName: null })} centered size="lg">
          <ModalHeader toggle={() => setConfirmModal({ open: false, queueName: null })}>
            Confirm Manual Trigger — {confirmJobInfo.title || confirmModal.queueName}
          </ModalHeader>
          <ModalBody>
            <p className="text-muted" style={{ fontSize: "13px" }}>
              {confirmJobInfo.description || "This job will be executed immediately."}
            </p>

            <div className="p-3 mb-3" style={{ background: "#f5f6fa", borderRadius: "4px", border: "1px solid #dbdfea" }}>
              <strong style={{ fontSize: "13px", color: "#364a63" }}>
                The following operations will be performed:
              </strong>
              <ol style={{ fontSize: "13px", color: "#526484", paddingLeft: "18px", marginTop: "8px", marginBottom: 0 }}>
                {(confirmJobInfo.steps || []).map((step, idx) => (
                  <li key={idx} style={{ marginBottom: "6px" }}>{step}</li>
                ))}
              </ol>
            </div>

            {confirmJobInfo.warning && (
              <div className="p-3 mb-3" style={{ background: "#ffeae9", borderRadius: "4px", border: "1px solid #e85347" }}>
                <Icon name="alert-circle" style={{ color: "#e85347", marginRight: "6px" }} />
                <span style={{ fontSize: "13px", color: "#e85347" }}>
                  <strong>Warning:</strong> {confirmJobInfo.warning}
                </span>
              </div>
            )}

            <div
              className="p-3 mb-3"
              style={{
                background: confirmJobInfo.safeToRerun ? "#e2f5ea" : "#fff3cd",
                borderRadius: "4px",
                border: `1px solid ${confirmJobInfo.safeToRerun ? "#1ee0ac" : "#ffc107"}`,
              }}
            >
              <span style={{ fontSize: "13px" }}>
                {confirmJobInfo.safeToRerun ? (
                  <Icon name="check-circle" style={{ color: "#1ee0ac", marginRight: "6px" }} />
                ) : (
                  <Icon name="alert" style={{ color: "#ffc107", marginRight: "6px" }} />
                )}
                {confirmJobInfo.safeNote}
              </span>
            </div>

            <p className="text-muted" style={{ fontSize: "12px", marginBottom: 0 }}>
              This triggers a one-time execution outside the regular schedule ({confirmJobInfo.schedule}).
              The regular schedule will continue as usual and is not affected.
            </p>
          </ModalBody>
          <ModalFooter className="bg-light">
            <Button color="light" size="sm" onClick={() => setConfirmModal({ open: false, queueName: null })}>
              Cancel
            </Button>
            <Button color="primary" size="sm" onClick={handleRetrigger}>
              <Icon name="play" style={{ marginRight: "4px" }} />
              Trigger Now
            </Button>
          </ModalFooter>
        </Modal>
      </Content>
    </React.Fragment>
  );
};

export default SchedulerDashboard;
